import * as DialogPrimitive from '@radix-ui/react-dialog'
import type { ComponentType, ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { Button } from './button'
import {
  escapeBelongsToOpenList,
  pressOriginIsDetached,
} from './dismiss-outside'
import { OverlayBody } from './overlay-body'
import { OverlayFooter } from './overlay-footer'
import { OverlayHeader } from './overlay-header'

const sizeWidths = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  fullscreen: 'max-w-none w-screen h-screen rounded-none',
} as const

export interface DialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  size?: keyof typeof sizeWidths
  title?: ReactNode
  eyebrow?: string
  icon?: ComponentType<{ size?: number; className?: string }>
  badge?: ReactNode
  bodyPadding?: 'none' | 'sm' | 'md' | 'lg'
  primaryLabel?: string
  primaryVariant?: 'primary' | 'danger'
  primaryDisabled?: boolean
  primaryLoading?: boolean
  /** Optional icon rendered before the primary label. */
  primaryIcon?: ReactNode
  onPrimary?: () => void
  secondaryLabel?: string
  onSecondary?: () => void
  /** Footer button size. Defaults to 'medium'; 'large' for touch/tablet flows. */
  footerSize?: 'medium' | 'large'
  trigger?: ReactNode
  dismissible?: boolean
  children?: ReactNode
}

export function Dialog({
  open,
  onOpenChange,
  size = 'md',
  title,
  eyebrow,
  icon,
  badge,
  bodyPadding,
  primaryLabel,
  primaryVariant = 'primary',
  primaryDisabled,
  primaryLoading,
  primaryIcon,
  onPrimary,
  secondaryLabel,
  onSecondary,
  footerSize = 'medium',
  trigger,
  dismissible = true,
  children,
}: DialogProps) {
  const hasFooter = primaryLabel || secondaryLabel
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {trigger && (
        <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>
      )}
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          // Named for the same reason as `sheet-scrim`.
          data-el="dialog-scrim"
          className={cn(
            'fixed inset-0 z-50 bg-black/40',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
          )}
        />
        <DialogPrimitive.Content
          onPointerDownOutside={(e) => {
            // See `pressOriginIsDetached`: a press that closed a layer
            // stacked over this one is not a press on this one's outside.
            if (!dismissible || pressOriginIsDetached(e)) e.preventDefault()
          }}
          onEscapeKeyDown={(e) => {
            if (!dismissible || escapeBelongsToOpenList(e)) e.preventDefault()
          }}
          className={cn(
            // `max-h` subtracts the iOS safe-area insets so a tall dialog
            // never extends under the status bar or home indicator on a
            // standalone PWA. The `1.5rem` is the desktop margin; the
            // `env(...)` terms collapse to 0 on devices without insets.
            'fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100vh-1.5rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] w-[calc(100vw-1.5rem)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg bg-surface-elevated shadow-modal overflow-hidden',
            'data-[state=open]:animate-in data-[state=open]:zoom-in-95 data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=closed]:fade-out-0',
            sizeWidths[size],
          )}
        >
          {(title || eyebrow || icon) && (
            <OverlayHeader
              title={title}
              titleAs={DialogPrimitive.Title}
              eyebrow={eyebrow}
              icon={icon}
              badge={badge}
              onClose={dismissible ? () => onOpenChange(false) : undefined}
            />
          )}
          <OverlayBody padding={bodyPadding}>{children}</OverlayBody>
          {hasFooter && (
            <OverlayFooter>
              {secondaryLabel && (
                <Button
                  variant="secondary"
                  size={footerSize}
                  onClick={onSecondary}
                >
                  {secondaryLabel}
                </Button>
              )}
              {primaryLabel && (
                <Button
                  variant={primaryVariant}
                  size={footerSize}
                  disabled={primaryDisabled}
                  loading={primaryLoading}
                  onClick={onPrimary}
                >
                  {primaryIcon}
                  {primaryLabel}
                </Button>
              )}
            </OverlayFooter>
          )}
          {/* Radix requires a Title inside Content. When the header renders
              one it IS that title (`titleAs` above), so this stands in only
              for a titleless overlay — rendering it unconditionally is what
              duplicated every heading. */}
          {!title && (
            <DialogPrimitive.Title className="sr-only">
              Dialog
            </DialogPrimitive.Title>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
